// File: src/components/TicketPurchasedSheet.jsx
import React, { useEffect, useRef, useState, useMemo, useContext } from 'react';
import { LuCheck } from 'react-icons/lu';
import { ReactComponent as TribeLogo } from '../../assets/TribeLogo.svg';
import paymentSuccessSound from '../../assets/Sound Effects/Payment-Success.mp3';
import { AuthContext } from '../../context/AuthContext';
import QrViewerSheet from './QrViewerSheet';

const isPWA = () =>
  (window.matchMedia?.('(display-mode: standalone)').matches) ||
  window.navigator.standalone === true;

/* ── helpers ─────────────────────────────────────────────── */
const monthDay = (d) =>
  d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });

const monthDayShort = (d) =>
  d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });

const hhmmss = (d) =>
  d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' });

const prettyMethod = (m) =>
  ({
    card: 'Credit Card',
    apple_pay: 'Apple Pay',
    google_pay: 'Google Pay',
    paypal: 'PayPal',
    free: 'Free',
  }[m] || 'Credit Card');

const money = (amt) => {
  const n = Number(amt || 0);
  return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
};

/* ── tiny confetti utils (burst from check) ───────────────── */
const randomColor = () =>
  ['#FFC107', '#E91E63', '#03A9F4', '#4CAF50', '#FF9800'][Math.floor(Math.random() * 5)];
const rand = (min, max) => Math.random() * (max - min) + min;
const makeParticles = (n) =>
  [...Array(n)].map((_, i) => {
    const ang = rand(0, Math.PI * 2);
    const d1 = rand(28, 54);
    const d2 = rand(70, 120);
    return {
      id: Date.now() + i,
      color: randomColor(),
      size: rand(5, 10),
      dur: rand(1.1, 1.8),
      x1: Math.cos(ang) * d1,
      y1: Math.sin(ang) * -d1,
      x2: Math.cos(ang) * d2,
      y2: Math.sin(ang) * -d2,
      r1: rand(0, 180),
      r2: rand(180, 360),
    };
  });

/* ── main component ──────────────────────────────────────── */
export default function TicketPurchasedSheet({
  event,
  fullName = '',
  paymentMethod = 'card',
  amount = 0,
  purchasedAt = Date.now(),
  orderId,
  qrSrc,
  celebrate = false,         // play audio/confetti only when purchasing now
  onClose,                   // not used for backdrop
  onShowEvent,
  onOpenQr
}) {
  const { currentUser } = useContext(AuthContext);

  const scrollYRef = useRef(0);
  const [particles, setParticles] = useState([]);
  const audioRef = useRef(null);
  const [showQr, setShowQr] = useState(false);

  // Resolve full name (always full last name)
  const resolvedFullName = useMemo(() => {
    if (typeof fullName === 'string' && fullName.trim()) return fullName.trim();

    if (currentUser?.firstName || currentUser?.lastName) {
      const nm = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim();
      if (nm) return nm;
    }

    const candidates = [
      { fn: event?.firstName,         ln: event?.lastName },
      { fn: event?.buyerFirstName,    ln: event?.buyerLastName },
      { fn: event?.user?.firstName,   ln: event?.user?.lastName },
      { fn: event?.attendeeFirstName, ln: event?.attendeeLastName },
    ];
    for (const c of candidates) {
      if (c.fn || c.ln) {
        const nm = `${c.fn || ''} ${c.ln || ''}`.trim();
        if (nm) return nm;
      }
    }

    try {
      const fn = localStorage.getItem('firstName') || '';
      const ln = localStorage.getItem('lastName') || '';
      const nm = `${fn} ${ln}`.trim();
      if (nm) return nm;
    } catch {}

    return '—';
  }, [fullName, currentUser, event]);

   // ⬇️ Freeze + theme-color (aligned with other sheets, safe with other overlays)
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = meta?.content;

    const alreadyLocked =
      document.body.classList.contains('overlay-active') ||
      document.body.style.position === 'fixed';

    const didLock = !alreadyLocked;

    // notch/status bar color
    if (meta) meta.content = '#999998';

    if (didLock) {
      scrollYRef.current = window.scrollY || window.pageYOffset || 0;

      // lock scroll using your global CSS rule
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.style.width = '100%';
      document.body.classList.add('overlay-active');

      // (optional, kept because you already use it elsewhere)
      document.documentElement.classList.add('overlay-active');
      document.getElementById('root')?.classList.add('overlay-active');
    }

    return () => {
      // restore notch/status bar to whatever it was
      if (meta) meta.content = prevTheme || '#ffffff';

      // only unlock if we were the ones who locked it
      if (didLock) {
        const y = scrollYRef.current || 0;

        document.body.classList.remove('overlay-active');
        document.documentElement.classList.remove('overlay-active');
        document.getElementById('root')?.classList.remove('overlay-active');

        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, y);
      }
    };
  }, []);

  // Play success audio on mount — only if celebrate=true
  useEffect(() => {
    if (!celebrate) return;
    const a = new Audio(paymentSuccessSound);
    audioRef.current = a;
    const t = setTimeout(() => a.play().catch(() => {}), 100);
    return () => {
      clearTimeout(t);
      try { a.pause(); } catch {}
    };
  }, [celebrate]);

  // Emit confetti — only if celebrate=true
  useEffect(() => {
    if (!celebrate) return;
    setParticles(makeParticles(26));
    const t = setTimeout(() => setParticles([]), 1800);
    return () => clearTimeout(t);
  }, [celebrate]);

  const when = new Date(purchasedAt);
  const addressLine1 = event?.address?.split(',')[0] || '';
  const addressRest = event?.address?.split(',').slice(1).join(',') || '';

  // ⬇️ UI text differences based on "viewing" vs "purchased now"
  const headingText = celebrate ? 'Purchase confirmed' : 'Ticket details';
  const ctaLabel    = celebrate ? 'Show Me' : 'Done';

  // ⬇️ Payment Method value: show "N/A" when free
  const isFree = Number(amount) === 0 || String(paymentMethod).toLowerCase() === 'free';
  const paymentMethodDisplay = isFree ? 'N/A' : prettyMethod(paymentMethod);

  return (
    <div style={S.backdrop} role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Ticket purchased"
        style={{
          ...S.sheet,
          paddingBottom: `calc(12px + env(safe-area-inset-bottom,0px) + ${isPWA() ? 8 : 0}px)`,
        }}
      >
        <div style={S.ticket}>
          {/* Side notches */}
          <div style={{ ...S.sideNotch, left: -18 }} />
          <div style={{ ...S.sideNotch, right: -18 }} />

          {/* Order ghost label (top-right) */}
          {orderId && (
            <div style={S.orderGhost}>
              Order Receipt
              <br />#{String(orderId)}
            </div>
          )}

          {/* Green check square + (optional) confetti + heading + helper */}
          <div style={S.headerWrap}>
            <div style={S.checkWrap}>
              <div style={S.checkSquare}>
                <LuCheck size={16} />
              </div>

              {/* Confetti burst anchored to the check */}
              <div style={S.confettiAnchor} aria-hidden>
                {particles.map((p) => (
                  <div
                    key={p.id}
                    style={{
                      ...S.confetti,
                      backgroundColor: p.color,
                      width: p.size,
                      height: p.size,
                      '--dur': `${p.dur}s`,
                      '--x1': `${p.x1}px`,
                      '--y1': `${p.y1}px`,
                      '--x2': `${p.x2}px`,
                      '--y2': `${p.y2}px`,
                      '--r1': `${p.r1}deg`,
                      '--r2': `${p.r2}deg`,
                    }}
                  />
                ))}
              </div>
            </div>

            <h2 style={S.h1}>{headingText}</h2>
            <p style={S.helper}>
              <strong style={{ fontWeight: 900 }}>Automatic check-in</strong>{' '}
              via phone location; show ticket if asked
            </p>
          </div>

          {/* Dotted separator */}
          <div style={S.sepDotted} />

          {/* Event summary block */}
          <div style={S.eventBlock}>
            <div style={S.eventName}>{event?.name || 'Event Name'}</div>
            <div style={S.eventLine}>
              <strong>{monthDay(new Date(event?.eventDate || when))}</strong>
              &nbsp;<span style={{ color: '#6b7280' }}>@</span>&nbsp;
              <strong>{event?.startTime12 || ''}</strong>
              &nbsp;-&nbsp;
              <strong>{event?.endTime12 || ''}</strong>
            </div>
            <div style={S.eventAddr}>
              <strong>{addressLine1}</strong>
              {addressRest ? <span>, {addressRest}</span> : null}
            </div>
          </div>

          {/* Divider */}
          <div style={S.sepRule} />

          {/* Details rows */}
          <div style={S.rows}>
            <Row label="Full Name" value={resolvedFullName} />
            <Row label="Date" value={monthDayShort(when)} />
            <Row label="Time" value={hhmmss(when)} />
            <Row label="Quantity" value="1" />
            <Row label="Payment Method" value={paymentMethodDisplay} />
            <Row label="Amount" value={money(amount)} bold />
          </div>

          {/* Grey line above QR section */}
          <div style={S.sepRule} />

          {/* Lower info card with QR area */}
          <div style={S.infoCard}>
            <div style={{ flex: 1 }}>
              <div style={S.infoCardTitle}>
                You’ll be checked in automatically; this QR code serves as a{' '}
                <span style={{ fontWeight: 800 }}>secondary verification </span>
                method if needed.
              </div>

              <div style={S.infoCardNote}>
                {orderId ? `Order Receipt #${orderId}` : 'Order Receipt —'}
              </div>
            </div>
            <div style={S.qrWrap}>
              {qrSrc ? (
                <img src={qrSrc} alt="QR code" style={S.qrImg} />
              ) : (
                <div style={S.qrPlaceholder} aria-hidden />
              )}
            </div>
          </div>

          {/* Grey line below QR section */}
          <div style={S.sepRule} />

          {/* Footer brand + CTA row */}
          <footer style={S.footerRow}>
            <TribeLogo style={{ width: 72, height: 'auto', opacity: 0.16 }} />
            <div style={{ flex: 1 }} />

            {/* Only show View QR when not celebrating (i.e. ticket details view) */}
            {!celebrate && (
              <button
                type="button"
                onClick={() => onOpenQr?.()}   // <-- only open QR via parent
                style={{
                  background: '#fff',
                  color: '#0b0b0b',
                  border: '1px solid #E7E9EE',
                  borderRadius: 8,
                  padding: '12px 18px',
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: 'pointer',
                  minWidth: 120,
                  marginRight: 8,
                }}
              >
                View QR
              </button>
            )}

            <button
              type="button"
              onClick={onShowEvent}
              style={S.primaryBtn}
            >
              {ctaLabel}
            </button>
          </footer>
        </div>

        {/* local keyframes */}
        <style>{`
          @keyframes slideUp { from { transform: translateY(12px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
          @keyframes confetti {
            0%   { transform: translate(0,0) rotate(0deg); opacity: 1; }
            45%  { transform: translate(var(--x1), var(--y1)) rotate(var(--r1)); opacity: 1; }
            100% { transform: translate(var(--x2), var(--y2)) rotate(var(--r2)); opacity: 0; }
          }
          @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important } }
          button:focus-visible { outline: 0; box-shadow: 0 0 0 3px rgba(59,130,246,.45) }
        `}</style>
      </div>

      {showQr && (
        <QrViewerSheet
          qrSrc={qrSrc}
          onBack={() => setShowQr(false)}
          onClose={() => setShowQr(false)}
        />
      )}
    </div>
  );
}

/* row subcomponent */
function Row({ label, value, bold }) {
  return (
    <div style={S.row}>
      <div style={S.rowLabel}>{label}</div>
      <div style={{ ...S.rowValue, ...(bold ? { fontWeight: 900, color: '#0b0b0b' } : {}) }}>
        {value}
      </div>
    </div>
  );
}

/* ───────────────────────── Styles */
const S = {
  backdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.40)', // aligned with other sheets
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    padding: 8,
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    pointerEvents: 'auto',
  },
  sheet: {
    width: '100%',
    maxWidth: 520,
    animation: 'slideUp .22s ease-out',
  },
  ticket: {
    position: 'relative',
    background: '#ffffff',
    borderRadius: 16,
    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
    padding: 28,
    paddingBottom: 16,
    minHeight: 'clamp(560px, 64vh, 780px)',
    display: 'flex',
    flexDirection: 'column',
  },
  sideNotch: {
    position: 'absolute',
    top: '22.5%',
    marginLeft: 2,
    marginRight: 2,
    width: 24,
    height: 24,
    background: '#797979',
    opacity: 1,
    borderRadius: '50%',
    transform: 'translateY(-50)',
  },
  orderGhost: {
    position: 'absolute',
    top: 18,
    right: 18,
    fontSize: 12,
    lineHeight: 1.25,
    color: '#d1d5db',
    fontWeight: 800,
    textAlign: 'right',
  },

  headerWrap: { paddingRight: 80 },
  checkWrap: { position: 'relative', width: 'fit-content' },
  checkSquare: {
    width: 28,
    height: 28,
    borderRadius: 8,
    background: '#E7F3E9',
    color: '#15803d',
    display: 'grid',
    placeItems: 'center',
    marginBottom: 8,
  },
  confettiAnchor: {
    position: 'absolute',
    inset: 0,
    transform: 'translate(0, 0)',
    pointerEvents: 'none',
  },
  confetti: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    borderRadius: 2,
    transformOrigin: 'center',
    animation: 'confetti var(--dur) ease-out forwards',
  },

  h1: {
    margin: 0,
    fontSize: 24,
    fontWeight: 900,
    color: '#0b0b0b',
  },
  helper: {
    margin: '8px 0 0px',
    fontSize: 14,
    lineHeight: 1.5,
    color: '#374151',
  },

  sepDotted: { borderTop: '1px dashed #e5e7eb', margin: '14px 0 10px' },
  sepRule: { borderTop: '1px solid #eef0f2', margin: '14px 0' },

  eventBlock: { marginTop: 2, marginBottom: 12 },
  eventName: { fontWeight: 900, color: '#111827', fontSize: 16, marginBottom: 6 },
  eventLine: { fontSize: 14, color: '#111827', marginBottom: 6 },
  eventAddr: { fontSize: 14, color: '#6b7280' },

  rows: { display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 12 },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  rowLabel: { fontSize: 14, color: '#6b7280' },
  rowValue: { fontSize: 14, color: '#111827', fontWeight: 700 },

  infoCard: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    borderRadius: 12,
    marginTop: 0,
  },
  infoCardTitle: {
    fontSize: 13,
    color: '#111827',
    fontWeight: 400,
    lineHeight: 1.4,
  },
  infoCardNote: { marginTop: 8, fontSize: 12, color: '#d1d5db', fontWeight: 800 },

  qrWrap: { width: 88, height: 88, borderRadius: 4, border: '1px solid #ddd',
    overflow: 'hidden', flexShrink: 0 },
  qrImg: { width: '100%', height: '100%', objectFit: 'cover' },
  qrPlaceholder: {
    width: '100%',
    height: '100%',
    background:
      'conic-gradient(from 45deg, #f3f4f6 0 25%, #e5e7eb 0 50%, #f3f4f6 0 75%, #e5e7eb 0) 0 0 / 16px 16px',
    border: '1px solid #e5e7eb',
    borderRadius: 0,
  },

  footerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 0,
  },
  primaryBtn: {
    background: '#0b0b0b',
    color: '#ffffff',
    border: '0px solid #0b0b0b',
    borderRadius: 8,
    padding: '12px 18px',
    fontSize: 12,
    fontWeight: 900,
    cursor: 'pointer',
    minWidth: 100,
  },
};
